import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TablePagination,
  Typography,
  Checkbox,
} from "@material-ui/core";
import brandTheme from "brandTheme/Theme";
import { useLocation } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  tableWrapper: {
    width: "96%",
    margin: "20px auto",
    marginBottom: "35px",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
    position: "relative",

    [theme.breakpoints.down(600)]: {
      width: "100%",
    },
  },

  tableContainer: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "550px",
    overflowY: "auto",
    borderRadius: "10px",
    backgroundColor: "#fff",
    position: "relative",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    backgroundColor: brandTheme.mainColor,
    color: "#ffffff",
    position: "sticky",
    top: 0,
    zIndex: 2,

    "&:hover": {
      backgroundColor: `${brandTheme.mainColor} !important`,
    },
  },

  row: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #ddd",
    transition: "background-color 0.2s ease-in-out",
    cursor: "pointer",

    "&:nth-child(even)": {
      backgroundColor: "#f7f7f7",
    },

    "&:hover": {
      backgroundColor: "#e3e3e3",
    },
  },

  selectedRow: {
    backgroundColor: "#eef5ff !important",
  },

  cell: {
    flex: 1,
    minWidth: "120px",
    padding: "8px 12px",
    textAlign: "left",
    fontSize: "14px",
    wordBreak: "break-word",
  },

  checkboxCell: {
    width: "55px",
    minWidth: "55px",
    maxWidth: "55px",
    flex: "0 0 55px",
    padding: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  paginationContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: "#fff",
    borderTop: "1px solid #ddd",
  },
}));

const AdminTable = ({
  columns,
  rows = [],
  onRowClick,
  styling,
  checkDate,
  searchValue,

  // NEW
  selectable = false,
  selectedRows = [],
  onSelectionChange,
}) => {
  const classes = useStyles();
  const location = useLocation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(()=>{
    setPage(0)
  },[location.state?.status,searchValue]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /*
   * Select one row
   */
  const handleSelectRow = (row) => {
    const leadId = Number(row.leadId);

    const alreadySelected = selectedRows.includes(leadId);

    if (alreadySelected) {
      onSelectionChange(
        selectedRows.filter((id) => id !== leadId)
      );
    } else {
      onSelectionChange([
        ...selectedRows,
        leadId,
      ]);
    }
  };

  /*
   * Select all rows on current page
   */
  const currentPageIds = paginatedRows.map((row) =>
    Number(row.leadId)
  );

  const allSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) =>
      selectedRows.includes(id)
    );

  const someSelected =
    currentPageIds.some((id) =>
      selectedRows.includes(id)
    ) && !allSelected;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      onSelectionChange([
        ...new Set([
          ...selectedRows,
          ...currentPageIds,
        ]),
      ]);
    } else {
      onSelectionChange(
        selectedRows.filter(
          (id) => !currentPageIds.includes(id)
        )
      );
    }
  };

  return (
    <div className={classes.tableWrapper}>
      <div className={classes.tableContainer}>

        {/* HEADER */}
        <div
          className={`${classes.row} ${classes.headerRow}`}
        >
          {selectable && (
            <div className={classes.checkboxCell}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                style={{
                  color: "#fff",
                }}
              />
            </div>
          )}

          {columns.map((column) => (
            <div
              key={column.id}
              className={classes.cell}
              style={{
                minWidth: column.minWidth || "120px",
              }}
            >
              {column.label}
            </div>
          ))}
        </div>

        {/* BODY */}
        {paginatedRows.length > 0 ? (
          paginatedRows.map((row, index) => {
            const leadId = Number(row.leadId);
            const isSelected =
              selectedRows.includes(leadId);
            return (
              <div
                key={index}
                className={`${classes.row} ${
                  isSelected
                    ? classes.selectedRow
                    : ""
                }`}
                onClick={() =>
                  onRowClick &&
                  onRowClick(row)
                }
              >
                {selectable && (
                  <div
                    className={classes.checkboxCell}
                    onClick={(event) =>
                      event.stopPropagation()
                    }
                  >
                    <Checkbox
                      checked={isSelected}
                      color="primary"
                      onChange={() =>
                        handleSelectRow(row)
                      }
                    />
                  </div>
                )}

                {columns.map((column) => (
                  <div
                    key={column.id}
                    className={classes.cell}
                    style={{
                      minWidth:
                        column.minWidth ||
                        "120px",
                    }}
                  >
                    
                    {column.render
                      ? column.render(
                          row,
                          index
                        )
                      : row[column.id]}
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: "30px",
              textAlign: "center",
            }}
          >
            <Typography color="textSecondary">
              No data available
            </Typography>
          </div>
        )}
      </div>

      {/* Pagination */}
      {rows.length > 0 && (
        <div className={classes.paginationContainer}>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 30, 50]}
            labelRowsPerPage="Rows per page:"
          />
        </div>
      )}
    </div>
  );
};

export default AdminTable;